import { LABEL_SIZE_PRESETS, parseLabelSize } from '@wms/domain';
import { useEffect, useState } from 'react';
import { isRotated, setRotated } from '../printing';

const HINTS: Readonly<Record<string, string>> = {
  '100x150': 'kargo etiketi rulosu',
  '100x100': 'kare',
  '100x50': 'raf etiketi',
  '50x30': 'ürün etiketi',
};

/**
 * The label roll in the printer: one of the usual sizes or any other, in millimetres. Only
 * sizes a 4-inch thermal printer takes are passed on (25–120 mm wide, 15–200 mm high).
 */
export function LabelSizePicker({ value, onChange, id, rotate = true }: { value: string; onChange: (size: string) => void; id?: string; rotate?: boolean }) {
  const preset = (LABEL_SIZE_PRESETS as readonly string[]).includes(value);
  const [custom, setCustom] = useState(!preset);
  const parsed = parseLabelSize(value);
  const [width, setWidth] = useState(parsed ? String(parsed.width) : '100');
  const [height, setHeight] = useState(parsed ? String(parsed.height) : '70');
  const typed = parseLabelSize(`${width}x${height}`);

  const typedSize = (w: string, h: string) => {
    setWidth(w);
    setHeight(h);
    const size = parseLabelSize(`${w}x${h}`);
    if (size) onChange(`${size.width}x${size.height}`);
  };

  return (
    <div className="stack" style={{ gap: 6 }}>
      <select
        id={id}
        className="select"
        value={custom ? 'custom' : value}
        onChange={(e) => {
          if (e.target.value === 'custom') {
            setCustom(true);
            typedSize(width, height);
          } else {
            setCustom(false);
            onChange(e.target.value);
          }
        }}
      >
        {LABEL_SIZE_PRESETS.map((s) => (
          <option key={s} value={s}>
            {s.replace('x', ' × ')} mm{HINTS[s] ? ` · ${HINTS[s]}` : ''}
          </option>
        ))}
        <option value="custom">Başka boyut…</option>
      </select>
      {custom && (
        <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
          <input className="input" inputMode="numeric" style={{ width: 84 }} value={width} onChange={(e) => typedSize(e.target.value.replace(/\D/g, ''), height)} aria-label="Etiket genişliği (mm)" />
          <span>×</span>
          <input className="input" inputMode="numeric" style={{ width: 84 }} value={height} onChange={(e) => typedSize(width, e.target.value.replace(/\D/g, ''))} aria-label="Etiket yüksekliği (mm)" />
          <span className="muted">mm</span>
        </div>
      )}
      {custom && !typed && <span className="hint bad">Genişlik 25–120 mm, yükseklik 15–200 mm olmalı</span>}
      {/* this computer's driver; another computer (a desk's printer) keeps its own */}
      {rotate && <RotateSwitch size={value} />}
    </div>
  );
}

/**
 * "Yatay bas": this computer prints labels of this size turned by 90°, for a driver that has the
 * roll the other way round (the label comes out sideways or shrunk).
 */
export function RotateSwitch({ size, label }: { size: string; label?: string }) {
  const [on, setOn] = useState(() => isRotated(size));
  useEffect(() => setOn(isRotated(size)), [size]);
  return (
    <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => {
          setRotated(size, e.target.checked);
          setOn(e.target.checked);
        }}
        style={{ marginTop: 3 }}
      />
      <span style={{ flex: '1 1 0', minWidth: 0 }}>
        {label ?? 'Yatay bas (90° döndür)'}
        <span className="muted" style={{ display: 'block', fontSize: 12.5 }}>
          Etiket yan ya da küçülmüş çıkıyorsa işaretleyin; bu bilgisayarda {size.replace('x', ' × ')} mm etiketlerin hepsi döndürülerek basılır.
        </span>
      </span>
    </label>
  );
}
